import { Schema, } from 'prosemirror-model';
import { SchemaType, DOMType, ChildrenDOMType, pluginDefaultPriority, } from '../constants.js';
import { ensureArray, uniq } from './array.js';
import { getSchemaType } from './schema-type.js';
import { truthy } from './truthy.js';
// 从插件列表和基础 spec 创建一个 prosemirror Schema 实例
export function createSchemaFromPlugins(plugins, docSpecs = []) {
    const exAttrs = [];
    const allNodeSpecs = plugins
        .map((plug) => {
        if (plug.spec && getSchemaType(plug.spec.type) === SchemaType.NODE) {
            return { ...plug.spec, name: plug.name };
        }
        return null;
    })
        .filter(truthy);
    plugins.forEach((plug) => {
        var _a, _b, _c;
        if (plug.spec) {
            const priority = (_b = (_a = plug.spec.priority) !== null && _a !== void 0 ? _a : plug.priority) !== null && _b !== void 0 ? _b : pluginDefaultPriority;
            switch (getSchemaType(plug.spec.type)) {
                case SchemaType.MARK: {
                    docSpecs.push({
                        marks: { [plug.name]: { ...plug.spec, priority } },
                    });
                    break;
                }
                case SchemaType.NODE: {
                    const spec = plug.spec;
                    const computeSpec = (spec) => {
                        var _a;
                        const { type, childrenType, inline } = spec;
                        const computedChildType = typeof childrenType === 'function'
                            ? childrenType(allNodeSpecs)
                            : childrenType;
                        const computedInline = inline !== null && inline !== void 0 ? inline : type === DOMType.INLINE_BLOCK;
                        const nodeSpec = {
                            inline: computedInline,
                            content: computedChildType === ChildrenDOMType.NONE
                                ? undefined
                                : computedChildType,
                            ...spec,
                            priority,
                        };
                        (_a = nodeSpec.group) !== null && _a !== void 0 ? _a : (nodeSpec.group = computedInline ? DOMType.INLINE : 'block');
                        return nodeSpec;
                    };
                    docSpecs.push({
                        nodes: { [plug.name]: computeSpec(spec) },
                    });
                    if ((_c = spec.dependencies) === null || _c === void 0 ? void 0 : _c.length) {
                        spec.dependencies.forEach((spec) => {
                            docSpecs.push({ nodes: { [spec.name]: computeSpec(spec) } });
                        });
                    }
                    break;
                }
            }
        }
        if (plug.specExtraAttrs) {
            exAttrs.push(plug.specExtraAttrs);
        }
    });
    const mergedDocSpec = mergeDocSpec(docSpecs, exAttrs);
    const schemaSpec = docSpec2SchemaSpec(mergedDocSpec);
    return new Schema(schemaSpec);
}
// 把 ExEditor mark|node spec 和 extraAttrs spec 组合成一体
export function mergeDocSpec(docSpecConfigs, docSpecExtraAttrsConfigs = []) {
    const ret = docSpecConfigs.reduce((ret, config) => {
        const { nodes, marks } = config;
        return {
            nodes: {
                ...ret.nodes,
                ...nodes,
            },
            marks: {
                ...ret.marks,
                ...marks,
            },
        };
    }, { nodes: {}, marks: {} });
    docSpecExtraAttrsConfigs.forEach((config) => {
        Object.keys(config).forEach((key) => {
            const attrSpec = config[key];
            const target = typeof attrSpec.target === 'string'
                ? [attrSpec.target]
                : attrSpec.target;
            target.forEach((nodeOrMarkOrGroup) => {
                var _a, _b;
                for (const [nodeName, node] of Object.entries(ret.nodes)) {
                    if (nodeName === nodeOrMarkOrGroup ||
                        ((_a = node.group) === null || _a === void 0 ? void 0 : _a.split(' ').includes(nodeOrMarkOrGroup))) {
                        node.attrs = {
                            ...node.attrs,
                            [key]: attrSpec,
                        };
                    }
                }
                for (const [markName, mark] of Object.entries(ret.marks)) {
                    if (markName === nodeOrMarkOrGroup ||
                        ((_b = mark.group) === null || _b === void 0 ? void 0 : _b.split(' ').includes(nodeOrMarkOrGroup))) {
                        // 对 matchStyle 的扩展比 matchSelector 更复杂一些，
                        // 需要把 extra parse 混合入 matchStyle 中
                        const matchStyleConf = mark.matchStyle;
                        if (config[key].parse && typeof matchStyleConf === 'object') {
                            Object.keys(matchStyleConf).forEach((styleKey) => {
                                const oldMatchStyle = matchStyleConf[styleKey];
                                matchStyleConf[styleKey] = function (value) {
                                    const oldRet = oldMatchStyle(value);
                                    if (oldRet === false) {
                                        return false;
                                    }
                                    const extraRet = config[key].parse(this);
                                    return extraRet === false
                                        ? false
                                        : { ...oldRet, [key]: extraRet };
                                };
                            });
                        }
                        if (config[key].parse && typeof matchStyleConf === 'string') {
                            const [styleKey, styleVal] = matchStyleConf.split('=');
                            mark.matchStyle = {
                                [styleKey](v) {
                                    if (v !== styleVal) {
                                        return false;
                                    }
                                    const extraRet = config[key].parse(this);
                                    return extraRet === false ? false : { [key]: extraRet };
                                },
                            };
                        }
                        mark.attrs = {
                            ...mark.attrs,
                            [key]: attrSpec,
                        };
                    }
                }
            });
        });
    });
    return ret;
}
// 将 ExEditor 的 spec 翻译成 prosemirror spec
// 要点是 attrs、parseDOM、toDOM 三个字段的转化
export function docSpec2SchemaSpec(docSpec) {
    // part 1. 处理 node spec
    const schemaSpecNodes = Object.keys(docSpec.nodes || {}).reduce((ret, nodeName) => {
        const docSpecNode = docSpec.nodes[nodeName];
        if (docSpecNode.raw) {
            return { ...ret, [nodeName]: docSpecNode };
        }
        const attributesNames = Object.keys(docSpecNode.attrs || {});
        return {
            ...ret,
            [nodeName]: {
                ...docSpecNode,
                exAttrs: docSpecNode.attrs,
                attrs: attributesNames.reduce((ret, attrName) => {
                    const attrDefault = docSpecNode.attrs[attrName].default;
                    return {
                        ...ret,
                        [attrName]: {
                            default: typeof attrDefault === 'function'
                                ? attrDefault()
                                : attrDefault,
                        },
                    };
                }, {}),
                parseDOM: [
                    {
                        tag: docSpecNode.matchSelector,
                        priority: docSpecNode.priority || 50,
                        getAttrs: ((node) => attributesNames.reduce((ret, attrName) => {
                            if (ret === false) {
                                return ret;
                            }
                            const attrParser = docSpecNode.attrs[attrName].parse ||
                                ((node) => node[attrName]);
                            const parsedAttribute = attrParser(node);
                            if (parsedAttribute === false) {
                                return false;
                            }
                            return {
                                ...ret,
                                [attrName]: parsedAttribute,
                            };
                        }, {})),
                        ...docSpecNode.matchOptions,
                    },
                ],
                toDOM: docSpecNode.toDOM
                    ? (node) => {
                        var _a;
                        let docSpecToDOM = docSpecNode.toDOM;
                        const mergedAttributes = renderAttrsToAttributes(node);
                        if (typeof docSpecToDOM === 'function') {
                            docSpecToDOM = docSpecToDOM(node, mergedAttributes);
                        }
                        if (typeof docSpecToDOM === 'string') {
                            docSpecToDOM = { tag: docSpecToDOM };
                        }
                        if (docSpecNode.content && 'tag' in docSpecToDOM) {
                            (_a = docSpecToDOM.children) !== null && _a !== void 0 ? _a : (docSpecToDOM.children = true);
                        }
                        return toDOMObjectToOutputSpec(docSpecToDOM, mergedAttributes);
                    }
                    : undefined,
            },
        };
    }, {});
    // part 2. 处理 mark spec
    const schemaSpecMarks = Object.keys(docSpec.marks || {}).reduce((ret, markName) => {
        const docSpecMark = docSpec.marks[markName];
        const attributesNames = Object.keys(docSpecMark.attrs || {});
        const baseGetAttrs = (node) => attributesNames.reduce((ret, attrName) => {
            if (ret === false) {
                return ret;
            }
            const attrParser = docSpecMark.attrs[attrName].parse ||
                ((node) => node[attrName]);
            const parsedAttribute = attrParser(node);
            if (parsedAttribute === false) {
                return false;
            }
            return {
                ...ret,
                [attrName]: parsedAttribute,
            };
        }, {});
        return {
            ...ret,
            [markName]: {
                ...docSpecMark,
                exAttrs: docSpecMark.attrs,
                attrs: attributesNames.reduce((ret, attrName) => {
                    const attrDefault = docSpecMark.attrs[attrName].default;
                    return {
                        ...ret,
                        [attrName]: {
                            default: typeof attrDefault === 'function'
                                ? attrDefault()
                                : attrDefault,
                        },
                    };
                }, {}),
                parseDOM: [
                    docSpecMark.matchSelector &&
                        {
                            tag: docSpecMark.matchSelector,
                            priority: docSpecMark.priority || 50,
                            getAttrs: baseGetAttrs,
                        },
                    typeof docSpecMark.matchStyle === 'string' &&
                        {
                            style: docSpecMark.matchStyle,
                            priority: docSpecMark.priority || 50,
                            consuming: false,
                        },
                    typeof docSpecMark.matchStyle === 'object' &&
                        Object.entries(docSpecMark.matchStyle).map(([key, value]) => ({
                            style: key,
                            priority: docSpecMark.priority || 50,
                            getAttrs: value,
                            consuming: false,
                        })),
                ]
                    .flat()
                    .filter(truthy),
                toDOM(node) {
                    var _a;
                    let docSpecToDOM = docSpecMark.toDOM;
                    const mergedAttributes = renderAttrsToAttributes(node);
                    if (typeof docSpecToDOM === 'function') {
                        docSpecToDOM = docSpecToDOM(node, mergedAttributes);
                    }
                    if (typeof docSpecToDOM === 'string') {
                        docSpecToDOM = { tag: docSpecToDOM };
                    }
                    if ('tag' in docSpecToDOM) {
                        (_a = docSpecToDOM.children) !== null && _a !== void 0 ? _a : (docSpecToDOM.children = true);
                    }
                    return toDOMObjectToOutputSpec(docSpecToDOM, mergedAttributes);
                },
            },
        };
    }, {});
    const schemaSpec = {
        topNode: 'doc',
        nodes: schemaSpecNodes,
        marks: schemaSpecMarks,
    };
    return schemaSpec;
}
export function renderAttrsToAttributes(node, base = {}) {
    return getMergedAttributes(node.attrs, node.type.spec.exAttrs, base);
}
// 按照 render spec 各属性渲染出来并进行组装
export function getMergedAttributes(attrs, attrsSpec = {}, base = {}) {
    const attributesList = Object.keys(attrsSpec).map((attrName) => {
        var _a, _b;
        let output = {};
        if (typeof attrsSpec[attrName].toDOMAttribute === 'function') {
            output =
                attrsSpec[attrName].toDOMAttribute(attrs[attrName], attrs) || {};
        }
        else {
            const rendered = ((_b = (_a = attrsSpec[attrName]).render) === null || _b === void 0 ? void 0 : _b.call(_a, attrs)) || {};
            output =
                typeof rendered === 'object' ? rendered : { [attrName]: rendered };
        }
        return output;
    });
    return mergeAttributes(attributesList, base);
}
// 组合 dom attributes，要点是 style 和 class 的拼装
export function mergeAttributes(attributesList, base = {}) {
    const ret = ensureArray(attributesList).reduce((ret, item) => {
        return {
            ...ret,
            ...item,
            style: item.style ? `${ret.style}; ${item.style}` : ret.style,
            class: item.class ? `${ret.class} ${item.class}` : ret.class,
        };
    }, { style: '', class: '', ...base });
    ret.style = uniq(ret.style.split(/\s*;\s*/).filter(truthy)).join('; ');
    ret.class = uniq(ret.class.split(/\s+/).filter(truthy)).join(' ');
    if (!ret.style) {
        delete ret.style;
    }
    if (!ret.class) {
        delete ret.class;
    }
    return ret;
}
// 把类似 { tag: 'div', attributes: {}, children: true }
// 转化成 prosemirror 的 model.DOMOutputSpec ['div', {}, 0]
export function toDOMObjectToOutputSpec(docSpecToDOM, mergedAttributes) {
    var _a;
    if (typeof docSpecToDOM === 'string') {
        return docSpecToDOM;
    }
    if ('dom' in docSpecToDOM) {
        return { dom: docSpecToDOM.dom };
    }
    if (!docSpecToDOM.tag) {
        throw new Error('missing tag for docSpec node');
    }
    const outputSpec = [
        docSpecToDOM.tag,
        (_a = docSpecToDOM.attributes) !== null && _a !== void 0 ? _a : mergedAttributes,
    ];
    if (docSpecToDOM.children === true) {
        outputSpec.push(0);
    }
    else if (docSpecToDOM.children) {
        ensureArray(docSpecToDOM.children).forEach((item) => {
            outputSpec.push(toDOMObjectToOutputSpec(item, mergedAttributes));
        });
    }
    return outputSpec;
}
//# sourceMappingURL=schema.js.map